import React, { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { BetaTestReport, DeployTest, DeployTestQuestion } from '../../types';
import { DeployProgram } from '../../config/deployProgram';
import { descriptionFromAnswer, descriptionFromQuestion } from './DeployTestActionItems';

const TASK_COLORS = {
  notTested: '#d1d5db',
  incomplete: '#60a5fa',
  completed: '#22c55e',
};

const STATUS_COLORS = {
  completed: '#22c55e',
  inProgress: '#60a5fa',
  notStarted: '#d1d5db',
};

type StatusBucket = 'completed' | 'inProgress' | 'notStarted';

interface StatusRow {
  key: string;
  label: string;
  fullLabel: string;
  completed: number;
  inProgress: number;
  notStarted: number;
  total: number;
}

const reportStatusBucket = (report: BetaTestReport): StatusBucket => {
  if (report.status === 'completed') return 'completed';
  if (report.status === 'not_received') return 'notStarted';
  return 'inProgress';
};

const reportPlatform = (report: BetaTestReport) => {
  if (report.phoneDevicePlatform === 'ios') return 'ios';
  if (report.phoneDevicePlatform === 'android') return 'android';
  return null;
};

const emptyBuckets = () => ({ completed: 0, inProgress: 0, notStarted: 0, total: 0 });

const countStatus = (reports: BetaTestReport[]) => (
  reports.reduce((counts, report) => {
    counts[reportStatusBucket(report)] += 1;
    counts.total += 1;
    return counts;
  }, emptyBuckets())
);

const buildStatusRows = (reports: BetaTestReport[]): StatusRow[] => {
  const ios = reports.filter((report) => reportPlatform(report) === 'ios');
  const android = reports.filter((report) => reportPlatform(report) === 'android');
  const rows: StatusRow[] = [
    { key: 'all', label: 'All tests', fullLabel: 'All tests', ...countStatus(reports) },
  ];
  if (ios.length) {
    rows.push({ key: 'ios', label: 'iOS', fullLabel: 'iOS', ...countStatus(ios) });
  }
  if (android.length) {
    rows.push({ key: 'android', label: 'Android', fullLabel: 'Android', ...countStatus(android) });
  }
  return rows;
};

const formatAnswer = (value: string, type?: string) => {
  if (!value) return '';
  if (type === 'boolean') {
    if (value === 'yes') return 'Yes';
    if (value === 'no') return 'No';
  }
  return value;
};

const formatPerson = (user?: BetaTestReport['user']) => {
  if (!user) return 'Unknown tester';
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return name || user.email || 'Unknown tester';
};

const phoneLabel = (report: BetaTestReport) => {
  const name = report.phoneDeviceName || 'Phone';
  if (!report.phoneDevicePlatform) return name;
  return `${name} (${report.phoneDevicePlatform === 'ios' ? 'iOS' : 'Android'})`;
};

const truncateLabel = (value: string, max = 42) => (
  value.length > max ? `${value.slice(0, max - 1)}…` : value
);

interface TaskRow {
  key: string;
  label: string;
  fullLabel: string;
  notTested: number;
  incomplete: number;
  completed: number;
  total: number;
}

interface AnswerRow {
  value: string;
  additionalInfo: string;
  tester: string;
  phone: string;
}

interface QuestionSummary {
  id: string;
  prompt: string;
  type: string;
  answers: AnswerRow[];
  buckets: Array<{ label: string; count: number }>;
  unanswered: number;
}

const buildTaskRows = (test: DeployTest | null, reports: BetaTestReport[]): TaskRow[] => (
  (test?.items || []).map((item, index) => {
    let notTested = 0;
    let incomplete = 0;
    let completed = 0;
    reports.forEach((report) => {
      if (report.status === 'not_received') {
        notTested += 1;
        return;
      }
      const response = (report.taskResponses || []).find((task) => String(task.itemId) === String(item._id));
      if (response?.done) completed += 1;
      else incomplete += 1;
    });
    const fullLabel = item.text || `Task ${index + 1}`;
    return {
      key: String(item._id || index),
      label: truncateLabel(fullLabel),
      fullLabel,
      notTested,
      incomplete,
      completed,
      total: reports.length,
    };
  })
);

const buildQuestionSummaries = (test: DeployTest | null, reports: BetaTestReport[]): QuestionSummary[] => (
  (test?.questions || []).map((question, index) => {
    const answers: AnswerRow[] = [];
    reports.forEach((report) => {
      if (report.status === 'not_received') return;
      const response = (report.questionResponses || []).find((entry) => (
        String(entry.questionId) === String(question._id)
      ));
      const value = String(response?.value || '').trim();
      const additionalInfo = String(response?.additionalInfo || '').trim();
      if (!value && !additionalInfo) return;
      answers.push({
        value,
        additionalInfo,
        tester: formatPerson(report.user),
        phone: phoneLabel(report),
      });
    });
    return {
      id: String(question._id || index),
      prompt: question.prompt || `Question ${index + 1}`,
      type: question.type,
      answers,
      buckets: answerBuckets(question, answers),
      unanswered: Math.max(reports.filter((report) => report.status !== 'not_received').length - answers.length, 0),
    };
  })
);

const answerBuckets = (question: DeployTestQuestion, answers: AnswerRow[]) => {
  if (question.type === 'text') return [];
  const counts = new Map<string, number>();
  const labels = question.type === 'boolean'
    ? ['Yes', 'No']
    : (question.choices || []).filter(Boolean);
  labels.forEach((label) => counts.set(label, 0));
  answers.forEach((answer) => {
    const label = formatAnswer(answer.value, question.type) || 'Other';
    counts.set(label, (counts.get(label) || 0) + 1);
  });
  return [...counts.entries()].map(([label, count]) => ({ label, count }));
};

const StatusTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: StatusRow }>;
}) => {
  if (!active || !payload?.[0]) return null;
  const row = payload[0].payload;
  return (
    <div className="deploy-test-overview-tooltip">
      <strong>{row.fullLabel}</strong>
      <div>Completed: {row.completed}</div>
      <div>In progress: {row.inProgress}</div>
      <div>Not started: {row.notStarted}</div>
    </div>
  );
};

const TaskTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: TaskRow }>;
}) => {
  if (!active || !payload?.[0]) return null;
  const row = payload[0].payload;
  return (
    <div className="deploy-test-overview-tooltip">
      <strong>{row.fullLabel}</strong>
      <div>Completed: {row.completed}</div>
      <div>Tested but not completed: {row.incomplete}</div>
      <div>Not started: {row.notTested}</div>
    </div>
  );
};

interface DeployTestResultsOverviewProps {
  test: DeployTest | null;
  reports: BetaTestReport[];
  program: DeployProgram;
  onCreateActionItem?: (preset: { title: string; description: string }) => void;
}

const DeployTestResultsOverview: React.FC<DeployTestResultsOverviewProps> = ({
  test,
  reports,
  program,
  onCreateActionItem,
}) => {
  const statusRows = useMemo(() => buildStatusRows(reports), [reports]);
  const taskRows = useMemo(() => buildTaskRows(test, reports), [test, reports]);
  const questions = useMemo(() => buildQuestionSummaries(test, reports), [test, reports]);
  const statusHeight = Math.max(180, statusRows.length * 52 + 88);
  const chartHeight = Math.max(240, taskRows.length * 52 + 88);
  const overall = statusRows[0];

  return (
    <div className="deploy-test-overview">
      <section className="card shadow-sm border-0 deploy-test-overview-widget">
        <div className="card-body">
          <div className="deploy-test-overview-widget-head">
            <h2>Tests</h2>
            <span>{overall ? `${overall.completed} of ${overall.total} completed` : 'No tests'}</span>
          </div>
          {reports.length === 0 ? (
            <div className="deploy-test-card-empty">No tests yet</div>
          ) : (
            <div style={{ width: '100%', height: statusHeight }}>
              <ResponsiveContainer>
                <BarChart data={statusRows} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="label"
                    width={90}
                    tick={{ fontSize: 12, fill: '#374151' }}
                  />
                  <Tooltip content={<StatusTooltip />} />
                  <Legend />
                  <Bar dataKey="completed" name="Completed" stackId="status" fill={STATUS_COLORS.completed} radius={[4, 0, 0, 4]} />
                  <Bar dataKey="inProgress" name="In progress" stackId="status" fill={STATUS_COLORS.inProgress} />
                  <Bar dataKey="notStarted" name="Not started" stackId="status" fill={STATUS_COLORS.notStarted} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {statusRows.some((row) => row.key !== 'all') && (
            <div className="deploy-test-overview-platform-stats">
              {statusRows.filter((row) => row.key !== 'all').map((row) => {
                const percent = row.total > 0 ? Math.round((row.completed / row.total) * 100) : 0;
                return (
                  <div key={row.key} className="deploy-test-overview-platform-stat">
                    <strong>{row.label}</strong>
                    <span>{row.completed} of {row.total} completed ({percent}%)</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="card shadow-sm border-0 deploy-test-overview-widget">
        <div className="card-body">
          <div className="deploy-test-overview-widget-head">
            <h2>Tasks</h2>
            <span>{taskRows.length} task{taskRows.length === 1 ? '' : 's'} · {reports.length} test{reports.length === 1 ? '' : 's'}</span>
          </div>
          {taskRows.length === 0 ? (
            <div className="deploy-test-card-empty">No tasks on this test</div>
          ) : (
            <div style={{ width: '100%', height: chartHeight }}>
              <ResponsiveContainer>
                <BarChart data={taskRows} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="label"
                    width={220}
                    tick={{ fontSize: 12, fill: '#374151' }}
                  />
                  <Tooltip content={<TaskTooltip />} />
                  <Legend />
                  <Bar dataKey="completed" name="Completed" stackId="tasks" fill={TASK_COLORS.completed} radius={[4, 0, 0, 4]} />
                  <Bar dataKey="incomplete" name="Tested but not completed" stackId="tasks" fill={TASK_COLORS.incomplete} />
                  <Bar dataKey="notTested" name="Not started" stackId="tasks" fill={TASK_COLORS.notTested} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </section>

      <section className="card shadow-sm border-0 deploy-test-overview-widget">
        <div className="card-body">
          <div className="deploy-test-overview-widget-head">
            <h2>Questions</h2>
            <span>{questions.length} question{questions.length === 1 ? '' : 's'}</span>
          </div>
          {questions.length === 0 ? (
            <div className="deploy-test-card-empty">No questions on this test</div>
          ) : (
            <div className="deploy-test-overview-questions">
              {questions.map((question) => {
                const answered = question.answers.length;
                const bucketTotal = question.buckets.reduce((sum, bucket) => sum + bucket.count, 0);
                return (
                  <article key={question.id} className="deploy-test-overview-question">
                    <div className="deploy-test-overview-question-head">
                      <div>
                        <div className="deploy-test-overview-question-prompt">{question.prompt}</div>
                        <div className="deploy-test-overview-question-meta">
                          {answered} answer{answered === 1 ? '' : 's'}
                          {question.unanswered > 0 ? ` · ${question.unanswered} unanswered` : ''}
                        </div>
                      </div>
                      {test && onCreateActionItem && (
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm deploy-test-card-task-action"
                          onClick={() => onCreateActionItem({
                            title: question.prompt,
                            description: descriptionFromQuestion(test, program, question),
                          })}
                        >
                          Create Action Item
                        </button>
                      )}
                    </div>
                    {question.buckets.length > 0 && (
                      <>
                        <div className="deploy-test-overview-answer-bar" aria-hidden={bucketTotal === 0}>
                          {question.buckets.map((bucket) => {
                            const width = bucketTotal > 0 ? (bucket.count / bucketTotal) * 100 : 0;
                            if (!bucket.count) return null;
                            return (
                              <span
                                key={bucket.label}
                                className="deploy-test-overview-answer-bar-seg"
                                style={{ width: `${width}%` }}
                                title={`${bucket.label}: ${bucket.count}`}
                              >
                                {width >= 18 ? `${bucket.label} ${bucket.count}` : ''}
                              </span>
                            );
                          })}
                        </div>
                        <div className="deploy-test-overview-answer-legend">
                          {question.buckets.map((bucket) => (
                            <span key={bucket.label}>
                              <strong>{bucket.count}</strong> {bucket.label}
                            </span>
                          ))}
                        </div>
                      </>
                    )}
                    {question.answers.length === 0 ? (
                      <div className="text-muted">No answers yet</div>
                    ) : (
                      <ul className="deploy-test-overview-answer-list">
                        {question.answers.map((answer, index) => (
                          <li key={`${question.id}-${index}`}>
                            <div className="deploy-test-overview-answer-body">
                              <div className="deploy-test-overview-answer-who">
                                {answer.tester}
                                <span>{answer.phone}</span>
                              </div>
                              {answer.value ? (
                                <div className="deploy-test-overview-answer-text">
                                  {formatAnswer(answer.value, question.type)}
                                </div>
                              ) : null}
                              {answer.additionalInfo ? (
                                <div className="deploy-test-overview-answer-note">{answer.additionalInfo}</div>
                              ) : null}
                            </div>
                            {test && onCreateActionItem && (
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm deploy-test-card-task-action"
                                onClick={() => onCreateActionItem({
                                  title: `${question.prompt} — ${answer.tester}`,
                                  description: descriptionFromAnswer(test, program, question, answer),
                                })}
                              >
                                Create Action Item
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default DeployTestResultsOverview;
